import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaNewspaper, FaSync, FaPause, FaPlay, FaBolt } from 'react-icons/fa';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const MarketNewsTicker = ({ selectedSymbol = 'all', onNewsUpdate }) => {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [isPaused, setIsPaused] = useState(false);
  const [breakingCount, setBreakingCount] = useState(0);
  const [selectedNews, setSelectedNews] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const tickerRef = useRef(null);
  const autoRefreshInterval = useRef(null);

  // Fetch news from backend
  const fetchNews = async (query = 'all') => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/india/market-news`, {
        params: { query }
      });

      if (response.data.success) {
        setNewsItems(response.data.data);
        setBreakingCount(response.data.data.filter(item => item.isBreaking).length);
        setError(null);
        
        // Notify parent component about news sentiment
        if (onNewsUpdate && response.data.overallSentiment) {
          onNewsUpdate(response.data.overallSentiment);
        }
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchNews(filter);
  }, [filter]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    autoRefreshInterval.current = setInterval(() => {
      if (!isPaused) {
        fetchNews(filter);
      }
    }, 60000); // 60 seconds

    return () => clearInterval(autoRefreshInterval.current);
  }, [filter, isPaused]);

  // Sync filter with parent's selected symbol
  useEffect(() => {
    if (selectedSymbol && selectedSymbol !== 'all') {
      setFilter(selectedSymbol);
    }
  }, [selectedSymbol]);

  // Handle manual refresh
  const handleRefresh = () => {
    fetchNews(filter);
  };

  // Open news modal
  const openNewsModal = (news) => {
    setSelectedNews(news);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedNews(null);
  };

  // Get sentiment color
  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'Positive': return 'text-green-600 bg-green-100';
      case 'Negative': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMinutes = Math.floor((now - time) / 1000 / 60);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return `${Math.floor(diffMinutes / 1440)}d ago`;
  };

  if (loading && newsItems.length === 0) {
    return (
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <FaNewspaper className="text-white text-2xl animate-pulse" />
          <span className="text-white font-semibold">Loading market news...</span>
        </div>
      </div>
    );
  }

  if (error && newsItems.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
        <div className="flex items-center gap-3">
          <FaNewspaper className="text-red-600 text-2xl" />
          <span className="text-red-800 font-semibold">{error}</span>
          <button
            onClick={handleRefresh}
            className="ml-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Main News Ticker Container */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl shadow-lg mb-6 overflow-hidden">
        {/* Header Controls */}
        <div className="bg-white/10 backdrop-blur-sm px-4 py-3 flex items-center justify-between border-b border-white/20">
          <div className="flex items-center gap-3">
            <FaNewspaper className="text-white text-xl" />
            <h3 className="text-white font-bold text-lg">Live Market News</h3>
            {breakingCount > 0 && (
              <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse flex items-center gap-1">
                <FaBolt className="text-xs" />
                {breakingCount} BREAKING
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Filter Dropdown */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 bg-white/20 text-white border border-white/30 rounded-lg text-sm font-medium hover:bg-white/30 transition-all focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              <option value="all" className="text-gray-800">All News</option>
              <option value="NIFTY" className="text-gray-800">NIFTY</option>
              <option value="BANKNIFTY" className="text-gray-800">BANKNIFTY</option>
              <option value="FINNIFTY" className="text-gray-800">FINNIFTY</option>
              <option value="SENSEX" className="text-gray-800">SENSEX</option>
              <option value="STOCKS" className="text-gray-800">Stocks</option>
              <option value="RBI" className="text-gray-800">RBI/Macro</option>
            </select>

            {/* Pause/Play Button */}
            <button
              onClick={() => setIsPaused(!isPaused)}
              className="p-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-all"
              title={isPaused ? 'Resume scrolling' : 'Pause scrolling'}
            >
              {isPaused ? <FaPlay /> : <FaPause />}
            </button>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-all disabled:opacity-50"
              title="Refresh news"
            >
              <FaSync className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Scrolling Ticker */}
        <div 
          className="relative overflow-hidden h-16 bg-gradient-to-r from-blue-700 to-purple-700"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div
            ref={tickerRef}
            className={`flex items-center h-full whitespace-nowrap ${
              isPaused ? '' : 'animate-ticker'
            }`}
            style={{
              animationDuration: `${newsItems.length * 5}s`,
              animationPlayState: isPaused ? 'paused' : 'running'
            }}
          >
            {newsItems.length === 0 ? (
              <div className="px-6 py-4 text-white text-sm">
                No news available for selected filter
              </div>
            ) : (
              newsItems.map((news, index) => (
                <div
                  key={`${news.id}-${index}`}
                  onClick={() => openNewsModal(news)}
                  className="inline-flex items-center gap-3 px-6 py-4 cursor-pointer hover:bg-white/10 transition-all"
                >
                  {/* Breaking Badge */}
                  {news.isBreaking && (
                    <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded animate-pulse">
                      BREAKING
                    </span>
                  )}

                  {/* Headline */}
                  <span className="text-white font-medium text-sm">{news.title}</span>

                  {/* Sentiment Badge */}
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getSentimentColor(news.sentiment)}`}>
                    {news.sentiment}
                  </span>

                  {/* Source & Time */}
                  <span className="text-white/70 text-xs">
                    {news.source} • {formatTimeAgo(news.publishedAt)}
                  </span>

                  {/* Separator */}
                  <span className="text-white/50 text-2xl mx-2">•</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* News Modal */}
      {showModal && selectedNews && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-2xl">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {selectedNews.isBreaking && (
                    <span className="inline-block px-3 py-1 bg-red-500 text-white text-xs font-bold rounded mb-3 animate-pulse">
                      🔥 BREAKING NEWS
                    </span>
                  )}
                  <h2 className="text-2xl font-bold text-white mb-2">{selectedNews.title}</h2>
                  <div className="flex items-center gap-3 text-white/80 text-sm">
                    <span>{selectedNews.source}</span>
                    <span>•</span>
                    <span>{formatTimeAgo(selectedNews.publishedAt)}</span>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="ml-4 p-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-all"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Sentiment & Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${getSentimentColor(selectedNews.sentiment)}`}>
                  Sentiment: {selectedNews.sentiment}
                </span>
                {selectedNews.symbolTags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Full Details */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-800 mb-2">News Details</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Published at: {new Date(selectedNews.publishedAt).toLocaleString()}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {selectedNews.title}
                </p>
              </div>

              {/* External Link */}
              {selectedNews.url && selectedNews.url !== '#' && (
                <a
                  href={selectedNews.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
                >
                  Read Full Article →
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CSS Animation for Ticker */}
      <style jsx>{`
        @keyframes ticker {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .animate-ticker {
          animation: ticker linear infinite;
        }
      `}</style>
    </>
  );
};

export default MarketNewsTicker;
